import { Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineProps,
  TimelineSeparator } from "@mui/lab";
import { Box, styled,
  Tooltip,
  Typography } from '@mui/material'
import { ElementType, FC, ReactChild } from "react";
import { BackupResponseDto } from "@/gen/api/backup";
import { formatDistanceStrict } from "date-fns/esm";
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import RestoreIcon from '@mui/icons-material/Restore'
import CheckIcon from '@mui/icons-material/Check' 
import { ReactFragment } from "react";
import { merge } from "lodash";
import { useUserTimeFormat } from "@/utils/userTimeFormat";


export interface BackupEventsTimeLineProps extends TimelineProps {
  backupRec?: BackupResponseDto;
}

const BackupTimeline = styled(Timeline)(({ theme }) => ({
  flexDirection: 'row',
  marginTop: 0,
  marginBottom: 0,
  '& .MuiTimelineItem-root': {
    flexDirection: 'column',
  },
  '& .MuiTimelineDot-root': {
    marginLeft: `${theme.spacing(1)}`,
    marginRight: `${theme.spacing(1)}`,
  },
  '& .MuiTimelineSeparator-root': {
    flexDirection: 'row',
  },
  '& .MuiTimelineConnector-root': {
    minHeight: 'unset',
    height: 2,
  },
}))

const BackupTimelineContent = styled('div')({
  padding: 0,
  position: 'absolute',
  width: 65,
  left: -12,
  lineHeight: 1,
  textAlign: 'center',
  marginTop: -10,
})


interface BackupEvent {
  name: string;
  title: ReactChild | ReactFragment;
  content?: string;
  time?: Date;
  timeDistanceStr?: string;
  scale: number | string;
  showConnector: boolean;
  active: boolean;
  isStart: boolean;
}

const defaultMap: Record<string, BackupEvent> = {
  "Backup Start": {
    name: 'Backup Start',
    title: '',
    scale: '0 0 40px',
    showConnector: true,
    active: false,
    isStart: true,
  },
  "Backup End": {
    name: 'Backup End',
    title: '',
    scale: '0 0 40px',
    showConnector: false,
    active: false,
    isStart: false,
  },
  "Validation Start": {
    name: 'Validation Start',
    title: '',
    scale: '0 0 40px',
    showConnector: true,
    active: false,
    isStart: true,
  },
  "Restore End": {
    name: 'Restore End',
    title: '',
    scale: '0 0 40px',
    showConnector: true,
    active: false,
    isStart: false,
  },
  "Validation End": {
    name: 'Validation End',
    title: '',
    scale: '0 0 40px',
    showConnector: false,
    active: false,
    isStart: false,
  },
}

const timelineItemCommon = (key: string, Icon: ElementType) => ({map}: { map: Record<string, BackupEvent>}) => {
  const backupEvent = map[key]
  return (
    <TimelineItem sx={{ flex: backupEvent.scale }}>
      <TimelineSeparator>
        <Tooltip title={backupEvent.title} placement="top" arrow disableHoverListener={!backupEvent.active}>
          <TimelineDot sx={{ bgcolor: backupEvent.active ? 'success.main' : 'text.disabled' }}>
            <Icon fontSize="smal" />
          </TimelineDot>
        </Tooltip>
        {backupEvent.showConnector ? <TimelineConnector /> : null}
      </TimelineSeparator>
      <TimelineContent>
        {backupEvent.content ?
          <BackupTimelineContent>
            <Typography
              variant="caption"
              sx={{ lineHeight: 1 }}
            >
              {backupEvent.content}
            </Typography>
          </BackupTimelineContent> : null
        }
      </TimelineContent> 
    </TimelineItem>
  )
}

const BackupStart = timelineItemCommon('Backup Start', PlayArrowIcon)
const BackupEnd = timelineItemCommon('Backup End', CloudUploadIcon)
const ValidationStart = timelineItemCommon('Validation Start', AutorenewIcon)
const RestoreEnd = timelineItemCommon('Restore End', RestoreIcon)
const ValidationEnd = timelineItemCommon('Validation End', CheckIcon)

const BackupEventsTimeLine: FC<BackupEventsTimeLineProps> = ({ backupRec, ...props }) => {
  const {
    createdTime,
    receivedTime,
    validationStartTime,
    restoreEndTime,
    validationEndTime,
  } = backupRec || {};

  const timePoints = ["Backup Start", "Backup End", "Validation Start", "Restore End", "Validation End"]
  
  const dates =
    [createdTime, receivedTime, validationStartTime, restoreEndTime, validationEndTime]
      .reduce<{ls:Date[], reduced:boolean}>((acc, timeStr) => {
        if (acc.reduced) return acc
        if (!timeStr) return { ...acc, reduced: true }
        try
        {
          acc.ls.push(new Date(timeStr))
          return acc
        }
        catch {
          return { ...acc, reduced: true }
        }
      }, { ls:[], reduced: false }).ls;

  const timeDiffs = dates.flatMap((d, i) =>
    dates[i + 1] ? [(dates[i + 1].getTime() - d.getTime())] : [])
  const baseline = Math.min(...timeDiffs)
  const skipScaleCalc = dates.length <= 3
  const timeProportions = 
    skipScaleCalc
    ? [1, 1, 1, 1, 1] 
    : timeDiffs.map(d => Math.floor(10 * d / baseline))
  const portionWhenMissing = 
    skipScaleCalc ? 0 :
    (Math.floor(
      timeProportions.reduce((a, b, i) => i === 1 ? a : a + b, 0)
      / (dates.length - 2)
    ))

  const format = useUserTimeFormat()
  const mapPatch =
    timePoints.reduce((acc, p, i) => {
      const predefined = defaultMap[p]
      const patch: Partial<BackupEvent> = {
        time: dates[i],
        showConnector: predefined.showConnector && !!dates[i + 1],
        active: !!dates[i],
      }
      if (dates[i] && dates[i - 1]) {
        patch.timeDistanceStr = formatDistanceStrict(dates[i], dates[i - 1])
      }

      if (predefined.isStart) {
        patch.content = dates[i] ? format(dates[i], 'Pp') : undefined
        patch.title = 
          <Typography variant="body2">
            {predefined.name}
          </Typography>
      } else {
        patch.title = 
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              {predefined.name}
            </Typography>
            <Typography variant="caption">
              {patch.timeDistanceStr}
            </Typography>
          </Box>
      }
      patch.scale = 
        predefined.showConnector 
        ? (timeProportions[i] || portionWhenMissing)
        : predefined.scale;
      
      acc[p] = patch
      return acc
    }, {} as Record<string, Partial<BackupEvent>>)
  const map = merge({}, defaultMap, mapPatch)

  return (
    <BackupTimeline sx={props.sx}>
      <BackupStart map={map} />
      <BackupEnd map={map} />
      <ValidationStart map={map} />
      <RestoreEnd map={map} />
      <ValidationEnd map={map} />
    </BackupTimeline>
  )
}

export default BackupEventsTimeLine;
